import React, { useState, useCallback, useRef } from 'react';
import { useMsal } from "@azure/msal-react";
import { SPEAuthProvider } from '../../services/speAuthProvider';
import { SharePointEmbeddedService } from '../../services/sharePointEmbeddedService';
import { legalDocumentService, LegalDocumentService } from '../../services/legalDocumentService';
import { settingsService } from '../../services/settingsService';
import { Matter } from '../../types';

// Azure best practice: Define constants at the top level for better maintainability
// Standard Container Type ID for production SharePoint Embedded
const SPE_CONTAINER_TYPE_ID = "94d2cf79-a208-4cc3-8ac7-1ff4348d3fb8";

// Note: Updated to use v1.0 endpoint instead of beta for better compatibility

interface PermissionCheck {
  id: string;
  name: string;
  granted: boolean;
  required: boolean;
}

interface TestResult {
  success: boolean;
  message: string;
  details?: any;
  error?: string;
}

/**
 * Comprehensive SharePoint Embedded test page
 * Combines authentication testing, permission verification, and container operations
 */
const SharePointEmbeddedTestPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Test results
  const [authResult, setAuthResult] = useState<TestResult | null>(null);
  const [permissionResult, setPermissionResult] = useState<PermissionCheck[]>([]);
  const [registrationResult, setRegistrationResult] = useState<TestResult | null>(null);
  const [containerResult, setContainerResult] = useState<TestResult | null>(null);
  const [createResult, setCreateResult] = useState<TestResult | null>(null);
  const [duplicatePreventionResult, setDuplicatePreventionResult] = useState<TestResult | null>(null);
  const [databasePersistenceResult, setDatabasePersistenceResult] = useState<TestResult | null>(null);
  const [cleanupResult, setCleanupResult] = useState<TestResult | null>(null);
  
  // Token information
  const [tokenInfo, setTokenInfo] = useState<any>(null);
  const [rawToken, setRawToken] = useState<string>("");
  
  // Container operations
  const containerNameRef = useRef<HTMLInputElement>(null);

  // Simple input handler for uncontrolled input
  const handleContainerNameChange = useCallback(() => {
    // Input value is managed by the DOM, no state update needed
  }, []);

  // Prevent form submission when Enter is pressed
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Don't call testContainerCreation here to avoid dependency issues
    }
  }, []);

  // Required permissions for SharePoint Embedded
  const REQUIRED_PERMISSIONS = [
    { id: "e1fe6dd8-ba31-4d61-89e7-88639da4683d", name: "User.Read", required: true },
    { id: "df85f4d6-205c-4ac5-a5ea-6bf408dba283", name: "Files.ReadWrite.All", required: true },
    { id: "205e70e5-aba6-4c52-a976-6d2d46c48043", name: "Sites.FullControl.All", required: true },
    { id: "2a72e471-7ed9-4a9f-87cb-ec5d08b93e36", name: "FileStorageContainer.Selected", required: true },
    { id: "5447fe39-cb82-4c1a-b977-520e67e724eb", name: "FileStorageContainer.Manage.All", required: true },
    { id: "863451e7-0667-486c-a5d6-d135439485f0", name: "Sites.Read.All", required: false },
    { id: "a154be20-db9c-4678-8ab7-66f6cc099a59", name: "User.ReadBasic.All", required: false }
  ];

  const testAuthentication = async () => {
    setLoading(true);
    setError(null);
    setAuthResult(null);
    setPermissionResult([]);
    setTokenInfo(null);
    setRawToken("");

    try {
      if (accounts.length === 0) {
        await instance.loginPopup({
          scopes: [
            "User.Read", 
            "Files.ReadWrite.All", 
            "Sites.FullControl.All", 
            "FileStorageContainer.Selected", 
            "FileStorageContainer.Manage.All"
          ]
        });
      }

      const account = instance.getAllAccounts()[0];
      
      // Get Graph API token for permission checking
      const response = await instance.acquireTokenSilent({
        scopes: [
          "User.Read", 
          "Files.ReadWrite.All", 
          "Sites.FullControl.All", 
          "FileStorageContainer.Selected", 
          "FileStorageContainer.Manage.All"
        ],
        account: account
      });

      // Decode token to check permissions
      const tokenPayload = JSON.parse(atob(response.accessToken.split('.')[1]));
      setTokenInfo(tokenPayload);
      setRawToken(response.accessToken);

      // Check scopes in token
      const grantedScopes = (tokenPayload.scp || tokenPayload.scope || "").split(" ");
      const scopeString = grantedScopes.join(", ");

      const permissionChecks = REQUIRED_PERMISSIONS.map(perm => ({
        ...perm,
        granted: grantedScopes.some((scope: string) => scope.includes(perm.name) || perm.name.includes(scope))
      }));

      setPermissionResult(permissionChecks);

      const missingRequired = permissionChecks.filter(p => p.required && !p.granted);
      
      // Test SharePoint token acquisition separately
      let sharePointTokenStatus = "Not tested";
      try {
        const sharePointUrl = "https://kizaninnovations.sharepoint.com";
        const sharePointTokenResponse = await instance.acquireTokenSilent({
          scopes: [`${sharePointUrl}/.default`],
          account: account
        });
        sharePointTokenStatus = sharePointTokenResponse.accessToken ? "✅ Available" : "❌ Failed";
      } catch (spError) {
        sharePointTokenStatus = `❌ Error: ${spError instanceof Error ? spError.message : String(spError)}`;
      }
      
      setAuthResult({
        success: missingRequired.length === 0,
        message: missingRequired.length === 0 
          ? "✅ Authentication successful with all required permissions"
          : `⚠️ Authentication successful but missing ${missingRequired.length} required permissions`,
        details: {
          account: account.name || account.username,
          scopes: scopeString,
          tokenExpiry: new Date(tokenPayload.exp * 1000).toISOString(),
          clientId: tokenPayload.appid || tokenPayload.aud,
          sharePointToken: sharePointTokenStatus
        }
      });

    } catch (err) {
      setAuthResult({
        success: false,
        message: "❌ Authentication failed",
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setLoading(false);
    }
  };

  const testContainerTypeRegistration = async () => {
    setLoading(true);
    setError(null);
    setRegistrationResult(null);

    try {
      // Get SharePoint-specific token instead of Graph token
      const accounts = instance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error("No authenticated accounts found");
      }

      // Request token with SharePoint as the audience
      const sharePointUrl = "https://kizaninnovations.sharepoint.com";
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: [`${sharePointUrl}/.default`],
        account: accounts[0]
      });

      const token = tokenResponse.accessToken;
      console.log("Using SharePoint-specific token for registration check");

      // Try multiple API endpoints to find the correct one
      const endpointsToTry = [
        `${sharePointUrl}/_api/v2.1/storageContainerTypes/${SPE_CONTAINER_TYPE_ID}/applicationPermissions`,
        `${sharePointUrl}/_api/v2.0/storageContainerTypes/${SPE_CONTAINER_TYPE_ID}/applicationPermissions`,
        `${sharePointUrl}/_api/storageContainerTypes/${SPE_CONTAINER_TYPE_ID}/applicationPermissions`,
        `${sharePointUrl}/_api/SharePointEmbedded/ContainerTypes/${SPE_CONTAINER_TYPE_ID}/ApplicationPermissions`
      ];

      let lastError = null;
      for (const endpoint of endpointsToTry) {
        try {
          console.log(`Trying endpoint: ${endpoint}`);
          
          const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (response.ok) {
            const containerTypePermissions = await response.json();
            setRegistrationResult({
              success: true,
              message: "✅ Container type is registered with application permissions",
              details: {
                containerTypeId: SPE_CONTAINER_TYPE_ID,
                applicationPermissions: containerTypePermissions.value || containerTypePermissions,
                workingEndpoint: endpoint,
                allEndpointsTried: endpointsToTry
              }
            });
            return; // Success, exit function
          } else if (response.status === 404) {
            setRegistrationResult({
              success: false,
              message: "❌ Container type not registered - registration required",
              error: "Container type has not been registered on this tenant",
              details: {
                containerTypeId: SPE_CONTAINER_TYPE_ID,
                httpStatus: response.status,
                workingEndpoint: endpoint,
                registrationRequired: "Use the registration API to register this container type",
                allEndpointsTried: endpointsToTry
              }
            });
            return; // Found the right endpoint but not registered
          } else {
            const errorText = await response.text();
            lastError = {
              endpoint,
              status: response.status,
              error: errorText
            };
            console.log(`Endpoint ${endpoint} failed with ${response.status}: ${errorText}`);
          }
        } catch (fetchError) {
          lastError = {
            endpoint,
            error: fetchError instanceof Error ? fetchError.message : String(fetchError)
          };
          console.log(`Endpoint ${endpoint} failed with error:`, fetchError);
        }
      }

      // If we get here, all endpoints failed
      setRegistrationResult({
        success: false,
        message: "❌ Container type registration check failed - API endpoint not found",
        error: "All API endpoints returned errors",
        details: {
          containerTypeId: SPE_CONTAINER_TYPE_ID,
          allEndpointsTried: endpointsToTry,
          lastError: lastError,
          suggestion: "The SharePoint Embedded API might not be available on this tenant or the container type ID might be invalid"
        }
      });

    } catch (err) {
      console.error("Container type registration test failed:", err);
      setRegistrationResult({
        success: false,
        message: "❌ Failed to check container type registration",
        error: err instanceof Error ? err.message : String(err),
        details: {
          containerTypeId: SPE_CONTAINER_TYPE_ID,
          suggestion: "Check network connectivity and authentication",
          note: "Registration uses SharePoint API, not Graph API"
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const registerContainerType = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get SharePoint-specific token instead of Graph token
      const accounts = instance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error("No authenticated accounts found");
      }

      // Request token with SharePoint as the audience
      const sharePointUrl = "https://kizaninnovations.sharepoint.com";
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: [`${sharePointUrl}/.default`],
        account: accounts[0]
      });

      const token = tokenResponse.accessToken;
      console.log("Using SharePoint-specific token for registration");

      const registrationPayload = {
        value: [
          {
            appId: "a0f3fad1-65f7-48fa-be4a-e93df9d033ad", // Your application ID
            delegated: ["full"],
            appOnly: ["full"]
          }
        ]
      };

      console.log("Registering container type with payload:", registrationPayload);

      const response = await fetch(
        `${sharePointUrl}/_api/v2.1/storageContainerTypes/${SPE_CONTAINER_TYPE_ID}/applicationPermissions`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(registrationPayload)
        }
      );

      if (response.ok) {
        const result = await response.json();
        setRegistrationResult({
          success: true,
          message: "✅ Container type registered successfully!",
          details: {
            containerTypeId: SPE_CONTAINER_TYPE_ID,
            applicationId: "a0f3fad1-65f7-48fa-be4a-e93df9d033ad",
            permissions: result.value || result,
            message: "Container type is now registered and ready for use"
          }
        });
      } else if (response.status === 403) {
        const errorText = await response.text();
        setRegistrationResult({
          success: false,
          message: "❌ Container type registration requires application permissions",
          error: `HTTP ${response.status}: ${errorText}`,
          details: {
            containerTypeId: SPE_CONTAINER_TYPE_ID,
            applicationId: "a0f3fad1-65f7-48fa-be4a-e93df9d033ad",
            httpStatus: response.status,
            issue: "SharePoint Embedded container type registration requires Application permissions (app-only context), not Delegated permissions (user context)",
            solution: "Add Application permissions to your app registration in Azure Portal",
            requiredApplicationPermissions: [
              "Sites.FullControl.All (Application)",
              "TermStore.ReadWrite.All (Application) - if needed"
            ],
            steps: [
              "1. Go to Azure Portal → App registrations → a0f3fad1-65f7-48fa-be4a-e93df9d033ad",
              "2. Click 'API permissions' → 'Add a permission' → 'SharePoint'",
              "3. Select 'Application permissions' (not Delegated)",
              "4. Add Sites.FullControl.All",
              "5. Click 'Grant admin consent for [tenant]'",
              "6. Wait a few minutes for permissions to propagate",
              "7. Try registration again"
            ],
            note: "Currently using delegated permissions which don't work for container type registration"
          }
        });
      } else {
        const errorText = await response.text();
        setRegistrationResult({
          success: false,
          message: "❌ Container type registration failed",
          error: `HTTP ${response.status}: ${errorText}`,
          details: {
            containerTypeId: SPE_CONTAINER_TYPE_ID,
            applicationId: "a0f3fad1-65f7-48fa-be4a-e93df9d033ad",
            httpStatus: response.status,
            payload: registrationPayload
          }
        });
      }
    } catch (err) {
      console.error("Container type registration failed:", err);
      setRegistrationResult({
        success: false,
        message: "❌ Failed to register container type",
        error: err instanceof Error ? err.message : String(err),
        details: {
          containerTypeId: SPE_CONTAINER_TYPE_ID,
          applicationId: "a0f3fad1-65f7-48fa-be4a-e93df9d033ad"
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const testGraphApiContainerTypes = async () => {
    setLoading(true);
    setError(null);

    try {
      const authProvider = new SPEAuthProvider();
      const token = await authProvider.getAccessToken();

      // Try Graph API endpoints for container types
      const graphEndpoints = [
        `https://graph.microsoft.com/v1.0/storage/fileStorage/containerTypes/${SPE_CONTAINER_TYPE_ID}`,
        `https://graph.microsoft.com/beta/storage/fileStorage/containerTypes/${SPE_CONTAINER_TYPE_ID}`,
        `https://graph.microsoft.com/v1.0/fileStorage/containerTypes/${SPE_CONTAINER_TYPE_ID}`,
        `https://graph.microsoft.com/beta/fileStorage/containerTypes/${SPE_CONTAINER_TYPE_ID}`
      ];

      let lastError = null;
      for (const endpoint of graphEndpoints) {
        try {
          console.log(`Trying Graph endpoint: ${endpoint}`);
          
          const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (response.ok) {
            const containerType = await response.json();
            setRegistrationResult({
              success: true,
              message: "✅ Container type found via Graph API",
              details: {
                containerTypeId: SPE_CONTAINER_TYPE_ID,
                containerType: containerType,
                workingEndpoint: endpoint,
                note: "Graph API access successful - container type exists"
              }
            });
            return;
          } else {
            const errorText = await response.text();
            lastError = {
              endpoint,
              status: response.status,
              error: errorText
            };
            console.log(`Graph endpoint ${endpoint} failed with ${response.status}: ${errorText}`);
          }
        } catch (fetchError) {
          lastError = {
            endpoint,
            error: fetchError instanceof Error ? fetchError.message : String(fetchError)
          };
        }
      }

      setRegistrationResult({
        success: false,
        message: "❌ Container type not accessible via Graph API",
        error: "All Graph API endpoints failed",
        details: {
          containerTypeId: SPE_CONTAINER_TYPE_ID,
          allEndpointsTried: graphEndpoints,
          lastError: lastError,
          note: "Graph API might not support this container type or it needs different permissions"
        }
      });

    } catch (err) {
      console.error("Graph API container type test failed:", err);
      setRegistrationResult({
        success: false,
        message: "❌ Failed to test Graph API container types",
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setLoading(false);
    }
  };

  const testContainerAccess = async () => {
    setLoading(true);
    setError(null);
    setContainerResult(null);

    try {
      const authProvider = new SPEAuthProvider();
      const speService = new SharePointEmbeddedService(authProvider);

      console.log("Testing container access with Container Type ID:", SPE_CONTAINER_TYPE_ID);

      const containers = await speService.getMatterContainers();
      const quotaInfo = await speService.getContainerQuotaInfo();
      
      setContainerResult({
        success: true,
        message: `✅ Retrieved ${containers.length} containers (${quotaInfo.availableSlots} slots available)`,
        details: {
          containerTypeId: SPE_CONTAINER_TYPE_ID,
          containerCount: containers.length,
          quotaLimit: quotaInfo.limit,
          availableSlots: quotaInfo.availableSlots,
          quotaWarning: quotaInfo.isNearLimit ? "⚠️ Near quota limit" : null,
          recycleBinNote: "Note: Deleted containers in recycle bin still count against quota",
          containers: containers.map(c => ({
            id: c.id,
            name: c.displayName,
            created: c.createdDateTime
          }))
        }
      });

    } catch (err) {
      console.error("Container access test failed:", err);
      setContainerResult({
        success: false,
        message: "❌ Container access failed",
        error: err instanceof Error ? err.message : String(err),
        details: {
          containerTypeId: SPE_CONTAINER_TYPE_ID,
          suggestion: "Check if container type is properly configured and permissions are granted"
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const testContainerPermissions = async () => {
    setLoading(true);
    setError(null);
    setContainerResult(null);

    try {
      const authProvider = new SPEAuthProvider();
      const speService = new SharePointEmbeddedService(authProvider);

      console.log("Testing container permissions...");

      // Get all containers
      const containers = await speService.getMatterContainers();
      
      if (containers.length === 0) {
        setContainerResult({
          success: false,
          message: "❌ No containers available for permission testing",
          details: { containerCount: 0 }
        });
        return;
      }

      // Test permissions on the first container
      const testContainer = containers[0];
      console.log("Testing permissions on container:", testContainer.displayName);

      // List current permissions
      const permissions = await speService.listContainerPermissions(testContainer.id);
      
      // Check if the configured group has permissions
      const hasGroupPermission = await speService.hasGroupPermission(testContainer.id);
      
      setContainerResult({
        success: true,
        message: `✅ Permission test completed for container: ${testContainer.displayName}`,
        details: {
          containerId: testContainer.id,
          containerName: testContainer.displayName,
          totalPermissions: permissions.length,
          hasConfiguredGroupPermission: hasGroupPermission,
          permissions: permissions.map(p => ({
            id: p.id,
            roles: p.roles,
            grantedTo: {
              type: p.grantedToV2.group ? 'Group' : 'User',
              id: p.grantedToV2.group?.id || p.grantedToV2.user?.id,
              displayName: p.grantedToV2.group?.displayName || p.grantedToV2.user?.displayName
            }
          }))
        }
      });

    } catch (err) {
      console.error("Container permission test failed:", err);
      setContainerResult({
        success: false,
        message: "❌ Container permission test failed",
        error: err instanceof Error ? err.message : String(err),
        details: {
          suggestion: "Check if you have proper permissions to view container permissions"
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const testContainerAccessDebug = async () => {
    setLoading(true);
    setError(null);
    setContainerResult(null);

    try {
      const authProvider = new SPEAuthProvider();
      const speService = new SharePointEmbeddedService(authProvider);

      console.log("Testing document access same as document tab...");

      // Create a test matter object to mimic how the document tab works
      const testMatter: Matter = {
        id: 'test-diagnostic-matter',
        title: 'Diagnostic Test Matter',
        clientId: 'test-client',
        type: 'general',
        status: 'active',
        description: 'Test matter for container diagnostic',
        speContainerId: undefined, // Will be set by ensureMatterContainer
        createdAt: new Date(),
        updatedAt: new Date(),
        dateCreated: new Date(),
        lastActivity: new Date(),
        billedAmount: 0
      };

      const results = {
        userInfo: null as any,
        groupSettings: null as any,
        documentTabTest: {
          ensureContainer: { success: false, containerId: null as string | null, error: null as any },
          listFiles: { success: false, documentCount: 0, error: null as any, documents: [] as any[] },
          permissions: { success: false, permissionCount: 0, error: null as any, permissions: [] as any[] }
        }
      };

      // Get current user info
      try {
        const token = await authProvider.getAccessToken();
        const userResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (userResponse.ok) {
          results.userInfo = await userResponse.json();
          console.log("Current user:", results.userInfo);
        }
      } catch (error) {
        console.warn("Failed to get user info:", error);
      }

      // Get group settings
      try {
        const settings = settingsService.getSharePointEmbeddedSettings();
        results.groupSettings = settings;
        console.log("Group settings:", settings);
      } catch (error) {
        console.warn("Failed to get group settings:", error);
      }

      // Test 1: Use exactly the same path as DocumentTab - ensureMatterContainer
      try {
        const legalDocService = new LegalDocumentService();
        console.log("Testing ensureMatterContainer (same as document tab)...");
        const { containerId } = await legalDocService.ensureMatterContainer(testMatter);
        results.documentTabTest.ensureContainer.success = true;
        results.documentTabTest.ensureContainer.containerId = containerId;
        console.log("✅ ensureMatterContainer successful, containerId:", containerId);

        // Test 2: Use exactly the same path as DocumentTab - listFiles
        try {
          console.log("Testing listFiles (same as document tab)...");
          const documents = await speService.listFiles(containerId);
          results.documentTabTest.listFiles.success = true;
          results.documentTabTest.listFiles.documentCount = documents.length;
          results.documentTabTest.listFiles.documents = documents.slice(0, 5); // First 5 docs
          console.log(`✅ listFiles successful, found ${documents.length} documents`);

          // Test 3: Check permissions on the working container
          try {
            console.log("Testing listContainerPermissions on working container...");
            const permissions = await speService.listContainerPermissions(containerId);
            results.documentTabTest.permissions.success = true;
            results.documentTabTest.permissions.permissionCount = permissions.length;
            results.documentTabTest.permissions.permissions = permissions;
            console.log(`✅ listContainerPermissions successful, found ${permissions.length} permissions`);
          } catch (error) {
            results.documentTabTest.permissions.error = error instanceof Error ? error.message : String(error);
            console.log("❌ listContainerPermissions failed:", error);
          }

        } catch (error) {
          results.documentTabTest.listFiles.error = error instanceof Error ? error.message : String(error);
          console.log("❌ listFiles failed:", error);
        }

      } catch (error) {
        results.documentTabTest.ensureContainer.error = error instanceof Error ? error.message : String(error);
        console.log("❌ ensureMatterContainer failed:", error);
      }

      // Build success message
      const successfulTests = [
        results.documentTabTest.ensureContainer.success && "Container Access",
        results.documentTabTest.listFiles.success && "Document Listing", 
        results.documentTabTest.permissions.success && "Permission Check"
      ].filter(Boolean);

      setContainerResult({
        success: results.documentTabTest.ensureContainer.success,
        message: successfulTests.length > 0 
          ? `🔍 Document Tab Path Test - ${successfulTests.join(', ')} successful (${successfulTests.length}/3 tests passed)`
          : "❌ Document Tab Path Test - All tests failed",
        details: results
      });

    } catch (err) {
      console.error("Document tab path test failed:", err);
      setContainerResult({
        success: false,
        message: "❌ Document tab path test failed",
        error: err instanceof Error ? err.message : String(err),
        details: {
          suggestion: "Check console for detailed error information"
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteTestContainers = async () => {
    if (!confirm("Are you sure you want to delete all test containers? This action cannot be undone.")) {
      return;
    }

    setLoading(true);
    setError(null);
    setCreateResult(null);

    try {
      const authProvider = new SPEAuthProvider();
      const speService = new SharePointEmbeddedService(authProvider);

      // Get all containers
      const containers = await speService.getMatterContainers();
      
      // Filter test containers (names starting with "Matter-test-" or "test-")
      const testContainers = containers.filter(c => 
        c.displayName?.includes("test-") || 
        c.displayName?.includes("Matter-test-") ||
        c.displayName?.includes("Test container")
      );

      if (testContainers.length === 0) {
        setCreateResult({
          success: true,
          message: "✅ No test containers found to delete",
          details: {
            totalContainers: containers.length,
            testContainersFound: 0
          }
        });
        return;
      }

      let deleted = 0;
      let errors = [];

      for (const container of testContainers) {
        try {
          // Note: Container deletion via Graph API
          const authProvider = new SPEAuthProvider();
          const token = await authProvider.getAccessToken();
          
          const response = await fetch(`https://graph.microsoft.com/v1.0/storage/fileStorage/containers/${container.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok || response.status === 204) {
            deleted++;
            console.log(`Deleted container: ${container.displayName} (${container.id})`);
          } else {
            const errorText = await response.text();
            errors.push(`Failed to delete ${container.displayName}: ${response.status} ${errorText}`);
          }
        } catch (err) {
          errors.push(`Error deleting ${container.displayName}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }

      setCreateResult({
        success: deleted > 0,
        message: deleted > 0 
          ? `✅ Deleted ${deleted} test containers${errors.length > 0 ? ` (${errors.length} errors)` : ''}`
          : `❌ Failed to delete test containers`,
        details: {
          totalTestContainers: testContainers.length,
          successfullyDeleted: deleted,
          errors: errors,
          remainingContainers: containers.length - deleted
        }
      });

      // Refresh container list
      await testContainerAccess();

    } catch (err) {
      console.error("Container deletion failed:", err);
      setCreateResult({
        success: false,
        message: "❌ Container deletion failed",
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setLoading(false);
    }
  };

  // Test matter container creation with duplicate prevention
  const testMatterContainerWithDuplicatePrevention = async () => {
    const matterId = "test-matter-" + Date.now();
    const matterTitle = "Test Matter for Duplicate Prevention";

    setLoading(true);
    setError(null);
    setDuplicatePreventionResult(null);
    
    try {
      const speAuthProvider = new SPEAuthProvider();
      const speService = new SharePointEmbeddedService(speAuthProvider);
      
      // Check quota first to avoid hitting limit
      const quotaInfo = await speService.getContainerQuotaInfo();
      console.log("Current quota info:", quotaInfo);
      
      if (quotaInfo.availableSlots === 0) {
        setDuplicatePreventionResult({
          success: false,
          message: "❌ Cannot test - trial quota limit reached",
          error: `Trial container type allows max ${quotaInfo.limit} containers. Current count: ${quotaInfo.currentCount}. Delete containers permanently from SharePoint Admin Center to free up quota.`
        });
        return;
      }
      
      // First check - should not find existing container with unique ID
      console.log("Testing duplicate prevention - Checking for existing container");
      const existingContainer1 = await speService.findMatterContainer(matterId);
      
      if (existingContainer1) {
        // Already exists, test passes by finding it
        console.log("✅ Found existing test container - reusing for test");
        setDuplicatePreventionResult({
          success: true,
          message: "✅ Duplicate prevention confirmed - found existing container",
          details: {
            matterId: matterId,
            foundExistingContainer: existingContainer1.id,
            containerName: existingContainer1.displayName,
            message: "Container already exists, demonstrating duplicate prevention"
          }
        });
        return;
      }
      
      // Create container only if quota allows
      console.log("Creating test container for duplicate prevention test");
      const container1 = await speService.createMatterContainer(
        matterId,
        matterTitle,
        'test-client-id',
        `Test container for duplicate prevention: ${matterTitle}`
      );
      console.log("Created container:", container1.id);
      
      // Second check - should now find the container we just created
      console.log("Testing duplicate prevention - Second search should find existing");
      const existingContainer2 = await speService.findMatterContainer(matterId);
      
      if (existingContainer2) {
        console.log("✅ Duplicate prevention working - found existing container:", existingContainer2.id);
        setDuplicatePreventionResult({
          success: true,
          message: "✅ Duplicate prevention test passed",
          details: {
            matterId: matterId,
            createdContainer: container1.id,
            foundExistingContainer: existingContainer2.id,
            containerName: existingContainer2.displayName,
            message: "Successfully created container, then found it on second check (preventing duplicate creation)"
          }
        });
      } else {
        console.log("❌ Duplicate prevention failed - search should have found the container we just created");
        setDuplicatePreventionResult({
          success: false,
          message: "❌ Duplicate prevention test failed",
          error: "Created container but subsequent search failed to find it"
        });
      }
      
    } catch (err) {
      console.error("Duplicate prevention test failed:", err);
      setError(`Duplicate prevention test failed: ${err instanceof Error ? err.message : String(err)}`);
      setDuplicatePreventionResult({
        success: false,
        message: "❌ Duplicate prevention test failed",
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setLoading(false);
    }
  };

  // Test database persistence integration with LegalDocumentService
  const testDatabasePersistence = async () => {
    setLoading(true);
    setError(null);
    setDatabasePersistenceResult(null);
    
    try {
      const legalDocService = legalDocumentService;
      
      // Create a mock matter for testing (without speContainerId initially)
      const testMatter: Matter = {
        id: "test-persistence-" + Date.now(),
        clientId: "test-client-123",
        title: "Database Persistence Test Matter",
        type: "Litigation",
        status: "active",
        description: "Test matter for database persistence verification",
        dateCreated: new Date(),
        lastActivity: new Date(),
        billedAmount: 0,
        createdAt: new Date(),
        updatedAt: new Date()
        // Note: speContainerId is intentionally undefined to test creation flow
      };

      console.log("🧪 Starting database persistence test with matter:", testMatter.id);

      // Step 1: First call should create container and save to database
      const firstResult = await legalDocService.ensureMatterContainer(testMatter);
      const firstContainerId = firstResult.containerId;
      const updatedMatter1 = firstResult.updatedMatter;

      if (!firstContainerId) {
        throw new Error("First container creation failed - no container ID returned");
      }

      console.log("✅ Step 1: Created container", firstContainerId);
      
      if (!updatedMatter1?.speContainerId) {
        console.warn("⚠️ Updated matter not returned from first call - this is acceptable but not optimal");
      }

      // Step 2: Simulate a matter with the persisted container ID (as it would come from database)
      const matterWithContainerId: Matter = {
        ...testMatter,
        speContainerId: firstContainerId
      };

      // Step 3: Second call should use existing container (no creation)
      const secondResult = await legalDocService.ensureMatterContainer(matterWithContainerId);
      const secondContainerId = secondResult.containerId;

      if (secondContainerId !== firstContainerId) {
        throw new Error(`Container ID mismatch! First: ${firstContainerId}, Second: ${secondContainerId}`);
      }

      console.log("✅ Step 2: Reused existing container", secondContainerId);

      // Step 4: Verify no duplicate containers were created by checking all matter containers
      const speAuthProvider = new SPEAuthProvider();
      const speService = new SharePointEmbeddedService(speAuthProvider);
      const allContainers = await speService.getMatterContainers();
      
      const testContainers = allContainers.filter(c => 
        c.displayName.includes(testMatter.id) || 
        c.displayName.includes("Database Persistence Test")
      );

      if (testContainers.length !== 1) {
        throw new Error(`Expected 1 container, found ${testContainers.length}. This indicates duplicate creation despite database persistence.`);
      }

      console.log("✅ Step 3: Verified no duplicates created");

      // Success!
      setDatabasePersistenceResult({
        success: true,
        message: "✅ Database persistence working correctly",
        details: {
          matterId: testMatter.id,
          containerId: firstContainerId,
          containersSteadyState: testContainers.length,
          persistenceVerified: firstContainerId === secondContainerId,
          summary: "Container created once, reused correctly, no duplicates found"
        }
      });

      console.log("🎉 Database persistence test PASSED - no duplicates created!");

    } catch (err) {
      console.error("Database persistence test failed:", err);
      setError(`Database persistence test failed: ${err instanceof Error ? err.message : String(err)}`);
      setDatabasePersistenceResult({
        success: false,
        message: "❌ Database persistence test failed",
        error: err instanceof Error ? err.message : String(err),
        details: {
          issue: "Container creation not properly persisted to database",
          suggestion: "Check API endpoint connectivity and database permissions"
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Clean up duplicate matter containers (keep only one per matter)
  const cleanupDuplicateMatterContainers = async () => {
    setLoading(true);
    setError(null);
    setCleanupResult(null);
    
    try {
      const speAuthProvider = new SPEAuthProvider();
      const speService = new SharePointEmbeddedService(speAuthProvider);
      
      // Get all matter containers
      const containers = await speService.getMatterContainers();
      
      // Group containers by matter ID
      const matterGroups = new Map<string, typeof containers>();
      
      containers.forEach(container => {
        // Extract matter ID from container name pattern: Matter-{matterId}-{title}
        const match = container.displayName.match(/^Matter-([^-]+)-/);
        if (match) {
          const matterId = match[1];
          if (!matterGroups.has(matterId)) {
            matterGroups.set(matterId, []);
          }
          matterGroups.get(matterId)!.push(container);
        }
      });
      
      let deletedCount = 0;
      let duplicatesFound = 0;
      const results: string[] = [];
      
      // Process each matter group
      for (const [matterId, matterContainers] of matterGroups) {
        if (matterContainers.length > 1) {
          duplicatesFound++;
          results.push(`\n📋 Matter ${matterId}: Found ${matterContainers.length} duplicate containers`);
          
          // Sort by creation date, keep the oldest
          matterContainers.sort((a, b) => new Date(a.createdDateTime).getTime() - new Date(b.createdDateTime).getTime());
          const keepContainer = matterContainers[0];
          const deleteContainers = matterContainers.slice(1);
          
          results.push(`   ✅ Keeping: ${keepContainer.displayName} (${keepContainer.createdDateTime})`);
          
          // Delete the duplicates
          for (const container of deleteContainers) {
            try {
              await speService.deleteContainer(container.id);
              deletedCount++;
              results.push(`   🗑️ Deleted: ${container.displayName} (${container.createdDateTime})`);
            } catch (deleteError) {
              results.push(`   ❌ Failed to delete: ${container.displayName} - ${deleteError instanceof Error ? deleteError.message : String(deleteError)}`);
            }
          }
        }
      }
      
      if (duplicatesFound === 0) {
        setCleanupResult({
          success: true,
          message: "✅ No duplicate matter containers found",
          details: {
            totalContainers: containers.length,
            uniqueMatters: matterGroups.size,
            message: "All matters have unique containers"
          }
        });
      } else {
        setCleanupResult({
          success: true,
          message: `🧹 Cleanup complete: Removed ${deletedCount} duplicates`,
          details: {
            duplicateMatters: duplicatesFound,
            containersDeleted: deletedCount,
            results: results.join('')
          }
        });
      }
      
      // Refresh container list
      await testContainerAccess();
      
    } catch (err) {
      setError(`Cleanup failed: ${err instanceof Error ? err.message : String(err)}`);
      setCleanupResult({
        success: false,
        message: "❌ Cleanup failed",
        error: err instanceof Error ? err.message : String(err)
      });
    } finally {
      setLoading(false);
    }
  };

  const testContainerCreation = async () => {
    const containerName = containerNameRef.current?.value?.trim() || "";
    if (!containerName) {
      setError("Please enter a container name");
      return;
    }

    setLoading(true);
    setError(null);
    setCreateResult(null);

    try {
      const authProvider = new SPEAuthProvider();
      const speService = new SharePointEmbeddedService(authProvider);

      console.log("Creating test container:", containerName);

      const container = await speService.createMatterContainer(
        "test-" + Date.now(),
        containerName,
        "test-client",
        "Test container created from debug page"
      );

      setCreateResult({
        success: true,
        message: "✅ Container created successfully",
        details: {
          containerId: container.id,
          name: container.displayName,
          created: container.createdDateTime
        }
      });

      // Clear the input field after successful creation
      if (containerNameRef.current) {
        containerNameRef.current.value = "";
      }

      // Refresh container list
      await testContainerAccess();

    } catch (err) {
      console.error("Container creation test failed:", err);
      setCreateResult({
        success: false,
        message: "❌ Container creation failed",
        error: err instanceof Error ? err.message : String(err),
        details: {
          requestedName: containerName,
          containerTypeId: SPE_CONTAINER_TYPE_ID
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(rawToken);
    alert("Token copied to clipboard");
  };

  const TestSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div style={{ 
      border: '1px solid #ddd', 
      borderRadius: '8px', 
      padding: '16px', 
      marginBottom: '16px',
      backgroundColor: '#f9f9f9'
    }}>
      <h3 style={{ margin: '0 0 12px 0', color: '#333' }}>{title}</h3>
      {children}
    </div>
  );

  const ResultDisplay = ({ result }: { result: TestResult | null }) => {
    if (!result) return null;
    
    return (
      <div style={{
        padding: '12px',
        borderRadius: '6px',
        marginTop: '12px',
        backgroundColor: result.success ? '#d4edda' : '#f8d7da',
        border: `1px solid ${result.success ? '#c3e6cb' : '#f5c6cb'}`
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
          {result.message}
        </div>
        {result.error && (
          <div style={{ color: '#721c24', fontSize: '14px', marginBottom: '8px' }}>
            Error: {result.error}
          </div>
        )}
        {result.details && (
          <details style={{ fontSize: '12px', color: '#495057' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Details</summary>
            <pre style={{ 
              margin: '8px 0 0 0', 
              padding: '8px', 
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '200px'
            }}>
              {JSON.stringify(result.details, null, 2)}
            </pre>
          </details>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <h1>SharePoint Embedded Integration Test</h1>
      <p style={{ color: '#666', marginBottom: '24px' }}>
        Comprehensive testing for SharePoint Embedded authentication, permissions, and container operations<br/>
        <strong>Local Server:</strong> http://localhost:3000
      </p>

      {error && (
        <div style={{
          padding: '12px',
          borderRadius: '6px',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          color: '#721c24',
          marginBottom: '16px'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Authentication Test */}
      <TestSection title="1. Authentication Test">
        <button 
          onClick={testAuthentication}
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Testing...' : 'Test Authentication & Permissions'}
        </button>
        
        <ResultDisplay result={authResult} />

        {/* Permission Matrix */}
        {permissionResult.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <h4>Permission Status:</h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#e9ecef' }}>
                  <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>Permission</th>
                  <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>Required</th>
                  <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {permissionResult.map((perm, index) => (
                  <tr key={index}>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>{perm.name}</td>
                    <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>
                      {perm.required ? '✓' : '○'}
                    </td>
                    <td style={{ 
                      padding: '8px', 
                      textAlign: 'center', 
                      border: '1px solid #ddd',
                      color: perm.granted ? 'green' : (perm.required ? 'red' : 'orange')
                    }}>
                      {perm.granted ? '✅ Granted' : '❌ Missing'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Token Information */}
        {tokenInfo && (
          <div style={{ marginTop: '16px' }}>
            <h4>Token Information:</h4>
            <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
              <div><strong>Client ID:</strong> {tokenInfo.appid || tokenInfo.aud}</div>
              <div><strong>Issued:</strong> {new Date(tokenInfo.iat * 1000).toLocaleString()}</div>
              <div><strong>Expires:</strong> {new Date(tokenInfo.exp * 1000).toLocaleString()}</div>
              <div><strong>Scopes:</strong> {tokenInfo.scp || tokenInfo.scope || 'None'}</div>
            </div>
            {rawToken && (
              <button
                onClick={copyToken}
                style={{
                  marginTop: '8px',
                  padding: '6px 12px',
                  fontSize: '12px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Copy Token to Clipboard
              </button>
            )}
          </div>
        )}
      </TestSection>

      {/* Container Type Registration Test */}
      <TestSection title="2. Container Type Registration Test">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
          <button 
            onClick={testContainerTypeRegistration}
            disabled={loading || !authResult?.success}
            style={{
              padding: '10px 20px',
              backgroundColor: '#ffc107',
              color: 'black',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || !authResult?.success) ? 'not-allowed' : 'pointer',
              opacity: (loading || !authResult?.success) ? 0.6 : 1
            }}
          >
            {loading ? 'Testing...' : 'Check Registration Status'}
          </button>
          
          <button 
            onClick={registerContainerType}
            disabled={loading || !authResult?.success}
            style={{
              padding: '10px 20px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || !authResult?.success) ? 'not-allowed' : 'pointer',
              opacity: (loading || !authResult?.success) ? 0.6 : 1
            }}
          >
            {loading ? 'Registering...' : 'Register Container Type'}
          </button>

          <button 
            onClick={testGraphApiContainerTypes}
            disabled={loading || !authResult?.success}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || !authResult?.success) ? 'not-allowed' : 'pointer',
              opacity: (loading || !authResult?.success) ? 0.6 : 1
            }}
          >
            {loading ? 'Testing...' : 'Test Graph API'}
          </button>
        </div>
        
        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
          This tests if the container type has been registered on your tenant via SharePoint API.<br/>
          <strong>Note:</strong> Trial container types from VSCode extension may not need registration and might not be accessible via these APIs.
        </div>
        
        <ResultDisplay result={registrationResult} />
      </TestSection>

      {/* Container Access Test */}
      <TestSection title="3. Container Access Test">
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
          <button 
            onClick={testContainerAccess}
            disabled={loading || !authResult?.success}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || !authResult?.success) ? 'not-allowed' : 'pointer',
              opacity: (loading || !authResult?.success) ? 0.6 : 1
            }}
          >
            {loading ? 'Testing...' : 'Test Container Access'}
          </button>

          <button 
            onClick={testContainerPermissions}
            disabled={loading || !authResult?.success}
            style={{
              padding: '10px 20px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || !authResult?.success) ? 'not-allowed' : 'pointer',
              opacity: (loading || !authResult?.success) ? 0.6 : 1
            }}
          >
            {loading ? 'Testing...' : 'Test Permissions'}
          </button>

          <button 
            onClick={deleteTestContainers}
            disabled={loading || !authResult?.success}
            style={{
              padding: '10px 20px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || !authResult?.success) ? 'not-allowed' : 'pointer',
              opacity: (loading || !authResult?.success) ? 0.6 : 1
            }}
          >
            {loading ? 'Deleting...' : 'Delete Test Containers'}
          </button>
        </div>
        
        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
          Container Type ID: {SPE_CONTAINER_TYPE_ID}<br/>
          <strong>Trial Limitation:</strong> Maximum 5 containers per tenant.<br/>
          <strong>⚠️ Important:</strong> Deleted containers go to recycle bin and still count against quota.<br/>
          <strong>To free quota:</strong> Permanently delete from SharePoint Admin Center &gt; Container Management.
        </div>
        
        <ResultDisplay result={containerResult} />
      </TestSection>

      {/* Container Access Debugging Test */}
      <TestSection title="3.1. Container Access Debugging Test">
        <button 
          onClick={testContainerAccessDebug} 
          disabled={loading}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#0078d4',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: '12px'
          }}
        >
          {loading ? 'Testing...' : '🔍 Test Container Access Methods'}
        </button>
        <div style={{ 
          backgroundColor: '#e3f2fd', 
          padding: '12px', 
          borderRadius: '6px', 
          fontSize: '14px', 
          marginBottom: '12px' 
        }}>
          <strong>Purpose:</strong> Comprehensive container access and permission debugging.<br/>
          <strong>What it tests:</strong><br/>
          • Direct container access (v1.0 vs beta endpoints)<br/>
          • Drive API verification and permission listings<br/>
          • Group permission configuration and user membership<br/>
          • User identity and Microsoft 365 Group membership validation<br/>
          <strong>Key insight:</strong> Helps identify if the issue is API endpoints, permission assignment, or group membership.
        </div>
        
        <ResultDisplay result={containerResult} />
      </TestSection>

      {/* Container Creation Test */}
      <TestSection title="4. Container Creation Test">
        <form onSubmit={(e) => e.preventDefault()} style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
          <input
            ref={containerNameRef}
            type="text"
            onChange={handleContainerNameChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter test container name"
            autoComplete="off"
            spellCheck={false}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              minWidth: '200px'
            }}
          />
          <button 
            type="button"
            onClick={testContainerCreation}
            disabled={loading || !authResult?.success}
            style={{
              padding: '8px 16px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (loading || !authResult?.success) ? 'not-allowed' : 'pointer',
              opacity: (loading || !authResult?.success) ? 0.6 : 1
            }}
          >
            {loading ? 'Creating...' : 'Create Test Container'}
          </button>
        </form>
        
        <ResultDisplay result={createResult} />
      </TestSection>

      {/* Duplicate Prevention Test */}
      <TestSection title="5. Matter Container Duplicate Prevention Test">
        <div style={{ marginBottom: '12px' }}>
          <p>This test verifies that the duplicate prevention logic works correctly by attempting to create a container twice for the same matter.</p>
        </div>
        
        <button 
          type="button"
          onClick={testMatterContainerWithDuplicatePrevention}
          disabled={loading || !authResult?.success}
          style={{
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (loading || !authResult?.success) ? 'not-allowed' : 'pointer',
            opacity: (loading || !authResult?.success) ? 0.6 : 1,
            marginBottom: '12px'
          }}
        >
          {loading ? 'Testing...' : 'Test Duplicate Prevention'}
        </button>
        
        <ResultDisplay result={duplicatePreventionResult} />
      </TestSection>

      {/* Database Persistence Test */}
      <TestSection title="6. Database Persistence Integration Test">
        <div style={{ marginBottom: '12px' }}>
          <p>This test verifies that container IDs are properly saved to the database to prevent duplicate container creation.</p>
          <p><strong>Test Flow:</strong> Creates container → Saves to database → Verifies reuse on subsequent calls</p>
        </div>
        
        <button 
          type="button"
          onClick={testDatabasePersistence}
          disabled={loading || !authResult?.success}
          style={{
            padding: '8px 16px',
            backgroundColor: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (loading || !authResult?.success) ? 'not-allowed' : 'pointer',
            opacity: (loading || !authResult?.success) ? 0.6 : 1,
            marginBottom: '12px'
          }}
        >
          {loading ? 'Testing...' : 'Test Database Persistence'}
        </button>
        
        <ResultDisplay result={databasePersistenceResult} />
      </TestSection>

      {/* Duplicate Cleanup */}
      <TestSection title="7. Cleanup Duplicate Matter Containers">
        <div style={{ marginBottom: '12px' }}>
          <p><strong>⚠️ Use with caution:</strong> This will delete duplicate containers for the same matter, keeping only the oldest one.</p>
          <p>This can help clean up the 4 duplicate containers you discovered for matter "Smith_v_Ford".</p>
        </div>
        
        <button 
          type="button"
          onClick={cleanupDuplicateMatterContainers}
          disabled={loading || !authResult?.success}
          style={{
            padding: '8px 16px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (loading || !authResult?.success) ? 'not-allowed' : 'pointer',
            opacity: (loading || !authResult?.success) ? 0.6 : 1,
            marginBottom: '12px'
          }}
        >
          {loading ? 'Cleaning...' : 'Cleanup Duplicate Containers'}
        </button>
        
        <ResultDisplay result={cleanupResult} />
      </TestSection>

      {/* Configuration Information */}
      <TestSection title="Configuration Information">
        <div style={{ fontSize: '14px', fontFamily: 'monospace' }}>
          <div><strong>Application ID:</strong> a0f3fad1-65f7-48fa-be4a-e93df9d033ad</div>
          <div><strong>Container Type ID:</strong> {SPE_CONTAINER_TYPE_ID}</div>
          <div><strong>Tenant ID:</strong> 2b0cb2a3-3262-4e5e-b6ab-a26e7847d88e</div>
          <div><strong>Environment:</strong> Standard (Production)</div>
        </div>
      </TestSection>

      {loading && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '20px',
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: 'white',
          borderRadius: '8px',
          zIndex: 1000
        }}>
          Testing in progress...
        </div>
      )}
    </div>
  );
};

export default SharePointEmbeddedTestPage;
